import React from "react";
import { CalendarDays, Scan, Coins, AlertCircle, ChevronRight, TrendingUp, ListTodo, CheckSquare, Activity, Wallet, ShieldAlert, ClipboardCheck, Award } from "lucide-react";
import { TitledPanel } from "@/portal/ui/Layout";
import { HEALTH_LEVEL, StatusDot as HealthDot } from "@/portal/ui/Badge";
import { fmtDateHuman, fmtDateLong } from "@/portal/format";

const HEALTH_DIMS = [["schedule", "Program", Activity], ["cost", "Cost", Wallet], ["risk", "Risc", ShieldAlert], ["decisions", "Decizii", ClipboardCheck], ["quality", "Calitate", Award]];

export default function OverviewTab({ project, portal, onGoTo }) {
  const pending = (portal?.actions || []).filter((a) => a.status === "pending");
  const nextDeadline = pending.map((a) => a.deadline).filter(Boolean).sort()[0];
  const lastUpdate = (portal?.updates || [])[0];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1.4fr)_minmax(0,0.85fr)] gap-5 items-start">
      <TitledPanel title="Despre proiect" testid="about-project">
        {project.welcome_message && <p className="cp-body italic border-l-2 border-cp-accent pl-3 mb-4 whitespace-pre-line">{project.welcome_message}</p>}
        <p className="cp-body whitespace-pre-line">{project.description || "Descrierea proiectului nu a fost încă publicată."}</p>
        <dl className="mt-5 pt-5 border-t border-cp-border grid grid-cols-3 gap-3">
          <Fact icon={CalendarDays} label="Data începerii" value={fmtDateHuman(project.start_date)} />
          <Fact icon={Scan} label="Suprafață" value={project.surface || "—"} border />
          <Fact icon={Coins} label="Valoare estimată" value={project.value || "—"} border />
        </dl>
      </TitledPanel>

      <div className="space-y-4">
        {pending.length > 0 && (
          <button type="button" onClick={() => onGoTo("decizii")} data-testid="overview-pending-cta"
            className="w-full cp-panel-amber px-4 py-3.5 flex items-center gap-3 text-left hover:bg-[#FBEBD0] transition-colors">
            <span className="h-8 w-8 rounded-full bg-cp-amber text-white grid place-items-center shrink-0"><AlertCircle className="h-4.5 w-4.5 h-[18px] w-[18px]" aria-hidden="true" /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-[14px] font-semibold text-cp-text">{pending.length} {pending.length === 1 ? "solicitare așteaptă decizia ta" : "solicitări așteaptă decizia ta"}</span>
              {nextDeadline && <span className="block text-[12.5px] text-cp-text-2">Termen limită: {fmtDateLong(nextDeadline)}</span>}
            </span>
            <ChevronRight className="h-5 w-5 text-cp-amber shrink-0" aria-hidden="true" />
          </button>
        )}
        <TitledPanel title="Ultima actualizare" testid="last-update"
          action={lastUpdate && <span className="cp-meta whitespace-nowrap">{fmtDateLong(lastUpdate.at)}</span>}>
          {lastUpdate ? (
            <dl className="divide-y divide-cp-border -mt-1">
              {lastUpdate.progress_this_week && <UpdateRow icon={TrendingUp} label="Progres" text={lastUpdate.progress_this_week} />}
              {lastUpdate.next_steps && <UpdateRow icon={ListTodo} label="Pași următori" text={lastUpdate.next_steps} />}
              {lastUpdate.client_actions && <UpdateRow icon={CheckSquare} label="Decizia ta" text={lastUpdate.client_actions} />}
            </dl>
          ) : <p className="cp-body-sm">Echipa Brisk nu a publicat încă o actualizare pentru acest proiect.</p>}
        </TitledPanel>
        <DeliveringCarousel />
      </div>

      <TitledPanel title="Starea proiectului în portofoliu" testid="health-index">
        <ul className="divide-y divide-cp-border -mt-1">
          {HEALTH_DIMS.map(([k, label, Icon]) => {
            const h = portal?.health?.[k] || { level: "On Track", note: "" };
            return (
              <li key={k} className="py-3 first:pt-0 last:pb-0" title={h.note || undefined}>
                <div className="flex items-center gap-3"><Icon className="h-4 w-4 text-cp-text-3 shrink-0" aria-hidden="true" /><span className="text-[13.5px] text-cp-text flex-1">{label}</span><HealthDot map={HEALTH_LEVEL} value={h.level} /></div>
                {h.note && <p className="cp-meta mt-1 pl-7">{h.note}</p>}
              </li>
            );
          })}
        </ul>
      </TitledPanel>
    </div>
  );
}

function Fact({ icon: Icon, label, value, border }) {
  return (
    <div className={`flex items-center gap-2.5 min-w-0 ${border ? "pl-3 border-l border-cp-border" : ""}`}>
      <Icon className="h-5 w-5 text-cp-text-2 shrink-0" aria-hidden="true" />
      <div className="min-w-0"><dt className="cp-meta">{label}</dt><dd className="text-[14px] font-semibold text-cp-text truncate">{value}</dd></div>
    </div>
  );
}

function UpdateRow({ icon: Icon, label, text }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 py-2.5">
      <dt className="flex items-center gap-2 text-[13px] text-cp-text-2"><Icon className="h-4 w-4 text-cp-text-3" aria-hidden="true" />{label}</dt>
      <dd className="text-[13.5px] text-cp-text leading-relaxed">{text}</dd>
    </div>
  );
}


const DELIVER_WORDS = ["excellence", "success", "projects"];
function DeliveringCarousel() {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % DELIVER_WORDS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="rounded-[var(--cp-radius-lg)] bg-cp-navy px-7 py-9 overflow-hidden select-none" data-testid="delivering-carousel" aria-label={`Delivering ${DELIVER_WORDS[i]} together`}>
      <div className="cp-serif leading-[1.08] text-white text-[38px] sm:text-[44px]">
        <div>Delivering</div>
        <div className="relative overflow-hidden" style={{ height: "1.15em" }} aria-hidden="true">
          <span key={i} className="cp-word-anim block leading-[1.15] text-[#7EA6E6]" data-testid="delivering-word">{DELIVER_WORDS[i]}</span>
        </div>
        <div>together</div>
      </div>
    </div>
  );
}
